import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2, Search } from 'lucide-react';
import { apiClient } from '@/core/api';

const ago = (iso: string) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const useOutside = (ref: React.RefObject<HTMLElement>, onOutside: () => void) => {
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onOutside(); };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onOutside(); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [ref, onOutside]);
};

interface Note { id: string; title: string; body?: string; link?: string | null; read: boolean; created_at: string }

/** The bell: unread count, the latest notifications, mark as read. Checks for new ones every minute. */
export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Note[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/notifications');
      setItems(res.data?.data?.items || []);
      setUnread(res.data?.data?.unread || 0);
    } catch { /* the bell stays quiet if the server cannot be reached */ }
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, 60_000);
    return () => window.clearInterval(t);
  }, [load]);
  useOutside(ref, useCallback(() => setOpen(false), []));

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) { setLoading(true); await load(); setLoading(false); }
  };
  const openNote = async (n: Note) => {
    setOpen(false);
    if (!n.read) {
      setItems(list => list.map(x => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread(u => Math.max(0, u - 1));
      apiClient.post(`/notifications/${n.id}/read`).catch(() => undefined);
    }
    if (n.link) navigate(n.link);
  };
  const readAll = async () => {
    setItems(list => list.map(x => ({ ...x, read: true })));
    setUnread(0);
    apiClient.post('/notifications/read-all').catch(() => undefined);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="icon-btn" title="Notifications" aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
        aria-expanded={open} onClick={toggle} style={{ position: 'relative' }}>
        <Bell size={18} />
        {unread > 0 && (
          <span style={{ position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 999, background: '#EA5455', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div role="dialog" aria-label="Notifications" style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 'min(360px, calc(100vw - 32px))', background: '#fff', border: '1px solid var(--clr-border)', borderRadius: 12, boxShadow: '0 12px 32px rgba(47,43,61,0.16)', zIndex: 300, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--clr-border)' }}>
            <strong style={{ color: 'var(--clr-text-heading)' }}>Notifications</strong>
            {unread > 0 && <button type="button" onClick={readAll} style={{ background: 'none', border: 0, color: 'var(--clr-primary)', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', display: 'inline-flex', gap: 4, alignItems: 'center' }}><CheckCheck size={14} /> Mark all read</button>}
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {loading && items.length === 0 ? (
              <div style={{ padding: 20, color: 'var(--clr-text-muted)', display: 'flex', gap: 8, alignItems: 'center' }}><Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>
            ) : items.length === 0 ? (
              <div style={{ padding: '28px 20px', textAlign: 'center', color: 'var(--clr-text-muted)', fontSize: 13.5 }}>You&apos;re all caught up.</div>
            ) : items.map(n => (
              <button key={n.id} type="button" onClick={() => openNote(n)}
                style={{ width: '100%', textAlign: 'left', display: 'flex', gap: 10, padding: '12px 16px', border: 0, borderBottom: '1px solid var(--clr-border)', background: n.read ? '#fff' : 'rgba(115,103,240,0.06)', cursor: n.link ? 'pointer' : 'default' }}>
                <span style={{ width: 8, minWidth: 8, height: 8, marginTop: 6, borderRadius: '50%', background: n.read ? 'transparent' : 'var(--clr-primary)' }} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontWeight: n.read ? 500 : 700, fontSize: 13.5, color: 'var(--clr-text-heading)' }}>{n.title}</span>
                  {n.body && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--clr-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.body}</span>}
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--clr-text-muted)', marginTop: 2 }}>{ago(n.created_at)}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface Hit { type: string; title: string; subtitle?: string; link: string }

/** Header search across what this account can open (students, applications, leads, consultancies...). */
export const HeaderSearch: React.FC<{ placeholder?: string; width?: number }> = ({ placeholder = 'Search…', width = 260 }) => {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  useOutside(ref, useCallback(() => setHits(null), []));

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits(null); return; }
    let cancelled = false;
    setBusy(true);
    const t = window.setTimeout(() => {
      apiClient.get(`/search?${new URLSearchParams({ q: term })}`)
        .then(res => { if (!cancelled) { setHits(res.data?.data?.results || []); setActive(0); } })
        .catch(() => { if (!cancelled) setHits([]); })
        .finally(() => { if (!cancelled) setBusy(false); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [q]);

  const go = (h: Hit) => { setHits(null); setQ(''); navigate(h.link); };
  const onKey = (e: React.KeyboardEvent) => {
    if (!hits?.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(hits.length - 1, a + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    if (e.key === 'Enter') { e.preventDefault(); go(hits[active]); }
  };

  return (
    <div ref={ref} style={{ position: 'relative', width }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 12px', border: '1px solid var(--clr-border)', borderRadius: 8, background: '#fff' }}>
        {busy ? <Loader2 size={15} color="#9092A9" style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={15} color="#9092A9" />}
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey} placeholder={placeholder} aria-label="Search"
          style={{ border: 0, outline: 0, flex: 1, minWidth: 0, fontSize: 13.5, background: 'transparent', color: 'var(--clr-text-heading)' }} />
      </div>
      {hits && (
        <div role="listbox" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 6px)', minWidth: 300, background: '#fff', border: '1px solid var(--clr-border)', borderRadius: 10, boxShadow: '0 12px 32px rgba(47,43,61,0.16)', zIndex: 300, maxHeight: 380, overflowY: 'auto' }}>
          {hits.length === 0 ? <div style={{ padding: 16, fontSize: 13, color: 'var(--clr-text-muted)' }}>No matches for “{q.trim()}”.</div> : hits.map((h, i) => (
            <button key={`${h.type}-${h.link}-${i}`} type="button" role="option" aria-selected={i === active} onMouseEnter={() => setActive(i)} onClick={() => go(h)}
              style={{ width: '100%', textAlign: 'left', display: 'grid', gap: 1, padding: '9px 14px', border: 0, background: i === active ? 'rgba(115,103,240,0.08)' : '#fff', cursor: 'pointer' }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--clr-primary)' }}>{h.type}</span>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--clr-text-heading)' }}>{h.title}</span>
              {h.subtitle && <span style={{ fontSize: 12, color: 'var(--clr-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.subtitle}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
